import {gql} from '@apollo/client'
import {useEffect, useMemo, useState} from 'react'
import {getClient} from '../lib/api'
import {useAuth} from '../lib/auth'
import {FeatureFlagsQuery} from './generated/use-flags'
export type Flags = {
  featurePreview?: boolean | null
  isInternal?: boolean | null
  cardConnectors?: boolean | null
  integrations?: boolean | null
  cardActions?: boolean | null
  actionFlows?: boolean | null
  authenticatedMagicLinks?: boolean | null
  cardMarketoConnector?: boolean | null
  insightsExclusionFilter?: boolean | null
  v2InsightsUsersFilter?: boolean | null
  clientKeyJwksUrl?: boolean | null
}

export const useFlags = (organisationId: string | undefined): Flags => {
  const {authState} = useAuth()
  const email = authState.authenticated && authState.email

  const [featureFlags, setFeatureFlags] = useState<Flags>({})

  useEffect(() => {
    if (email && organisationId) {
      getClient(organisationId)
        .query<FeatureFlagsQuery>({
          query: FEATURE_FLAGS_QUERY,
          variables: {email, organisationId},
          fetchPolicy: 'cache-first'
        })
        .then(d => {
          setFeatureFlags(d.data.featureFlags ?? {})
        })
    }
  }, [organisationId, email, setFeatureFlags])

  const cardConnectors = organisationId
    ? organisationId.startsWith('59-') ||
      organisationId.startsWith('53-') ||
      organisationId.startsWith('241-') ||
      organisationId.startsWith('215-') ||
      organisationId.startsWith('415-1') ||
      organisationId.startsWith('414-1') ||
      organisationId.startsWith('446-1') ||
      organisationId.startsWith('120-') ||
      organisationId.startsWith('492-1') ||
      organisationId.startsWith('493-1')
    : false

  // Salesforce Marketing Cloud connector (sfmc)
  const integrations = organisationId
    ? organisationId.startsWith('53-') ||
      organisationId.startsWith('59-') ||
      organisationId.startsWith('241-') ||
      organisationId.startsWith('215-') ||
      organisationId.startsWith('415-1') ||
      organisationId.startsWith('414-1') ||
      organisationId.startsWith('446-1') ||
      organisationId.startsWith('120-') ||
      organisationId.startsWith('492-1') ||
      organisationId.startsWith('493-1') ||
      organisationId.startsWith('496-') ||
      organisationId.startsWith('508-')
    : false

  // Enable Card actions menu in configuration screen
  const cardActions = organisationId
    ? !!organisationId.startsWith('62-') || !!organisationId.startsWith('147-')
    : false

  // Enable for ANZ
  const authenticatedMagicLinks =
    !!organisationId && organisationId.startsWith('62-')

  const enableMarketoSSFSConnector =
    !!organisationId && organisationId.startsWith('59-')

  const insightsExclusionFilter = organisationId
    ? organisationId.startsWith('147-')
    : false

  // As with insightsExclusionFilter above, this filter is only for westpac - do not enable for other customers, do not tell other customers about it.
  // Unless they explicitly ask for such a filter.
  // IMO it's a bit of a redundant filter since we already allow you to filter out test data - but they have insisted upon this.
  // I'm not sure how it's going to perform, so don't want it widely enabled.
  const v2InsightsUsersFilter = organisationId
    ? organisationId.startsWith('147-')
    : false

  // enable preview for demo for govt project org 2326-1
  const previewForDemoOrgs = organisationId
    ? organisationId.startsWith('2326-')
    : false

  const clientKeyJwksUrl = organisationId
    ? organisationId.startsWith('508-')
    : false

  // cache output in the event it is used in effects
  const flags = useMemo(
    () => ({
      ...featureFlags,
      featurePreview: featureFlags.featurePreview || previewForDemoOrgs,
      cardConnectors,
      cardActions,
      authenticatedMagicLinks,
      integrations,
      cardMarketoConnector:
        !!featureFlags.featurePreview || enableMarketoSSFSConnector,
      insightsExclusionFilter,
      v2InsightsUsersFilter,
      clientKeyJwksUrl
    }),
    [
      featureFlags,
      cardConnectors,
      cardActions,
      authenticatedMagicLinks,
      integrations,
      enableMarketoSSFSConnector,
      insightsExclusionFilter,
      v2InsightsUsersFilter,
      previewForDemoOrgs,
      clientKeyJwksUrl
    ]
  )

  return flags
}

export const FEATURE_FLAGS_QUERY = gql`
  query FeatureFlags($email: String, $organisationId: String) {
    featureFlags(email: $email, organisationId: $organisationId) {
      featurePreview
      isInternal
    }
  }
`
